import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function BitcoinTechPage77() {
  const contentRef = useRef(null);
  const titleText = "Bitcoin Mining and Proof of Work";
  const links = {
    prev: "/bitcoin-tech-page-76",
    index: "/index-bitcoin-tech",
    next: "/bitcoin-tech-page-78",
  };

  const questions = [
    {
      text: "1. Why is the term 'mining' used in the context of Bitcoin?",
      options: [
        {
          value: "A",
          label: "A. Because bitcoins are physically extracted from computers",
        },
        {
          value: "B",
          label:
            "B. Because it resembles the process of investing real resources to extract scarce value",
        },
        {
          value: "C",
          label: "C. Because miners choose which users receive new bitcoins",
        },
        {
          value: "D",
          label: "D. Because it involves solving useful scientific equations",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What primary role does proof of work play in Bitcoin?",
      options: [
        {
          value: "A",
          label: "A. It allows miners to control Bitcoin’s monetary policy",
        },
        {
          value: "B",
          label: "B. It ensures transactions are encrypted for privacy",
        },
        {
          value: "C",
          label:
            "C. It secures the network by making attacks economically expensive",
        },
        {
          value: "D",
          label: "D. It guarantees miners equal rewards",
        },
      ],
      correct: "C",
    },
    {
      text: "3. What happens when the total hash power of the Bitcoin network increases?",
      options: [
        {
          value: "A",
          label: "A. Blocks are mined faster permanently",
        },
        {
          value: "B",
          label: "B. The block reward increases automatically",
        },
        {
          value: "C",
          label:
            "C. The difficulty adjusts to keep block times near 10 minutes",
        },
        {
          value: "D",
          label: "D. Full nodes reduce validation requirements",
        },
      ],
      correct: "C",
    },
    {
      text: "4. Who ultimately enforces Bitcoin’s consensus rules?",
      options: [
        {
          value: "A",
          label: "A. Miners with the most hash power",
        },
        {
          value: "B",
          label: "B. Developers who write the Bitcoin software",
        },
        {
          value: "C",
          label: "C. Mining pools coordinating block production",
        },
        {
          value: "D",
          label:
            "D. Full nodes that independently validate blocks and transactions",
        },
      ],
      correct: "D",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={77}
        timerMinutes={60}
        currentLevel={6}
        unlockedBadges={11}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 77</b>! In this lesson, we explore Bitcoin mining in
        depth, moving beyond the surface idea of “creating new coins” to
        understand mining as the engine that secures the network, regulates
        monetary issuance, and keeps Bitcoin decentralized. You will learn how
        proof of work converts real-world energy into digital security, how
        miners and full nodes interact through incentives and rules, and why
        mining is best understood as a carefully designed game of probability
        with profound economic consequences. By the end, you will see why miners
        serve the network rather than control it, and why mining remains a
        cornerstone of Bitcoin’s resilience.
        <br />
        The word mining in the context of Bitcoin is not chosen arbitrarily. It
        draws its inspiration from the age-old process of extracting precious
        resources like gold or silver from the earth. Just as miners of the past
        would invest labor and tools to unearth valuable metals hidden
        underground, Bitcoin miners dedicate energy and computational power to
        bring new bitcoins into existence. But in the digital world of Bitcoin,
        there's no pickaxe or shovel, the tools are processors, and the effort
        lies in solving complex mathematical problems.
        <br />
        But Bitcoin mining is far more than just a method to create new coins.
        It serves a dual purpose. Alongside issuing new bitcoins, mining plays a
        foundational role in protecting the network from attacks and keeping the
        entire system decentralized. Without mining, Bitcoin wouldn’t be the
        resilient, borderless form of money it is today.
        <br />
        <br />
        <b>Mining as Value Conversion</b>
        <br />
        <br />
        Bitcoin mining can be thought of as a modern form of alchemy, a process
        that transforms off chain resources into on chain value. Through mining,
        electricity and capital are funneled into a mechanism that produces
        digital scarcity in the form of bitcoin. This transformation operates on
        multiple conceptual levels, all describing the same underlying process.
        <br />
        At the most tangible level, mining involves computers performing
        calculations, specifically hashing, to solve cryptographic puzzles.
        These machines consume real world energy, producing what we call work.
        As we zoom out, this activity represents the economic exchange of energy
        and hardware costs for bitcoin, and at the highest level of abstraction,
        we refer to it simply as mining, the act of converting external inputs
        into digital assets.
        <br />
        This process is fundamental to Bitcoin’s value proposition. It ensures
        that bitcoin is not created arbitrarily. Unlike fiat currencies, miners
        cannot inflate the supply, manipulate monetary policy, or alter
        consensus rules. Those rules are enforced strictly by full nodes, which
        validate all transactions and blocks according to a shared protocol.
        <br />
        Miners play a crucial role by confirming transactions and securing the
        blockchain through proof of work. However, their power is limited. While
        they can in rare scenarios produce misleading confirmations, such as
        during chain reorganizations, they cannot override the network’s
        consensus or forge new bitcoin out of thin air. The authority to
        determine what is valid lies with the collective enforcement of rules by
        full nodes, not with the computational power of miners.
        <br />
        <br />
        <b>Mining Secures the Bitcoin Network</b>
        <br />
        <br />
        At its core, Bitcoin is a peer-to-peer network that functions without a
        central authority. This makes it different from traditional banking
        systems, which rely on centralized servers and institutions to validate
        and settle transactions. In Bitcoin, consensus is achieved through
        mining, a mechanism called proof-of-work.
        <br />
        Mining is essential for Bitcoin's security and decentralization. Miners
        are the participants who validate transactions and add them to the
        public ledger, known as the blockchain. To do so, they compete by
        solving a computational "puzzle". This process requires significant
        electricity and processing power, making it expensive to perform and
        nearly impossible to fake at scale. This ensures that only those willing
        to expend real-world resources can influence the blockchain, which
        disincentivizes malicious behavior.
        <br />
        If a single miner or group wanted to manipulate the blockchain, perhaps
        by spending the same bitcoin twice (a “double spend” attack), they would
        need to redo the proof-of-work for not just one block, but for every
        block that follows. This would require an unimaginable amount of
        computing power and electricity, making such an attack prohibitively
        costly and practically infeasible. This is how mining maintains
        Bitcoin’s integrity and decentralization.
        <br />
        <br />
        <b>Solving the Puzzle: The Mining Process</b>
        <br />
        <br />
        So what exactly is the puzzle that miners are trying to solve? <br />
        <br />
        Every 10 minutes, on average, miners compete to discover a number called
        a <b>nonce</b> that, when plugged into the block's data and passed
        through a cryptographic function called SHA-256, results in a hash value
        that starts with a certain number of leading zeroes. This hash is
        unpredictable and entirely dependent on the input data, so miners can
        only find the correct nonce by trial and error. This process is like a
        brute-force, where thousands of machines are constantly guessing nonces
        as fast as they can.
        <br />
        Once a miner finds a valid hash, they broadcast the new block containing
        a list of pending transactions, to the network. Other nodes quickly
        verify the block’s validity, and if it checks out, the block becomes
        part of the blockchain. <br />
        The successful miner is rewarded with two things: <br />
        <br />
        the coinbase reward, which is new bitcoins created with the block, and
        the transaction fees paid by users whose transactions are included.
        <br />
        <br />
        The coinbase reward halves approximately every four years, in an event
        called the Bitcoin Halving. In 2009, the reward started at 50 bitcoins
        per block. It halved to 25 in 2012, 12.5 in 2016, 6.25 in 2020, and as
        of 2024, it’s 3.125. This controlled issuance mimics the scarcity of
        precious metals and ensures that only 21 million bitcoins will ever
        exist.
        <br />
        <br />
        But how is it that a block is mined every 10 minutes, despite the fact
        that the network’s total computational power keeps increasing?
        <br />
        <br />
        That’s where the difficulty adjustment algorithm comes in. Every 2016
        blocks, roughly every two weeks the network recalculates the mining
        difficulty. If blocks are being mined faster than every 10 minutes, the
        difficulty increases, making the puzzle harder. If blocks are coming in
        too slowly, the difficulty decreases. This elegant feedback loop keeps
        Bitcoin on track and ensures a steady, predictable supply of new coins.
        <br />
        <br />
        <b>Understanding Proof of Work as a Game of Chance with Purpose</b>
        <br />
        <br />
        Proof of Work is often described as a computer puzzle with adjustable
        difficulty. A helpful way to understand it is to imagine a Rubiks Cube
        for machines — challenging to solve but simple to verify once a solution
        is found. When miners generate a block, they are essentially trying to
        find the right combination called the nonce that produces a hash meeting
        the network's current difficulty target.
        <br />
        However, this process is frequently misunderstood. People might think
        miners are solving meaningful math problems. In truth, they are running
        a game of probability. The hashes they produce are not solving real
        world problems, but instead serve as a record of the energy spent and
        effort committed. Each hash is like rolling digital dice — it may or may
        not result in a win.
        <br />
        Miners are not trying to solve a problem in the traditional sense. They
        are proving they have expended a certain amount of computational effort.
        The more hashes a miner produces, or the more times they roll the dice,
        the greater their chance of winning the right to add the next block.
        This game is designed to be winnable within a predictable amount of
        time, which helps maintain the regular rhythm of new blocks appearing on
        the network.
        <br />
        To illustrate, imagine a dice game with one player using a single six
        sided die. They need to roll a one to win, which gives them a one in six
        chance. If more players join, say six in total, the odds that at least
        one of them wins in a given round rise dramatically. To keep the average
        time to win consistent, the rules of the game can be adjusted — for
        instance, requiring two ones instead of one. As difficulty increases
        with more participants, the system ensures blocks are still produced
        roughly every ten minutes.
        <br />
        This adjustment happens every 2016 blocks, which is about once every two
        weeks, and is known as the difficulty adjustment. It helps balance the
        network as miners join or leave. If more miners compete, difficulty
        rises. If miners drop out, it becomes easier to win. This feedback loop
        is part of the Bitcoin timechain design, which regulates block
        production over time.
        <br />
        The goal for each miner is individual success, but the protocol is
        designed to keep block intervals steady regardless of how many players
        there are. More miners do not mean blocks are found faster. Instead,
        difficulty simply scales to match the collective hashing power.
        <br />
        In essence, Proof of Work ensures that while individual miners pursue
        personal rewards, the network as a whole maintains order and
        consistency. It is not about solving arbitrary puzzles, but about
        regulating the heartbeat of Bitcoin — one block at a time.
        <br />
        <br />
        <b>Building the Blockchain</b>
        <br />
        <br />
        Each mined block does not exist in isolation. It contains a reference,
        specifically, the hash of the previous block. This creates a chain of
        blocks, a blockchain, starting from the very first block ever created by
        Satoshi Nakamoto, called the genesis block.
        <br />
        This chaining of blocks is fundamental to Bitcoin’s security. Imagine
        trying to alter the data in a block from the past. Because each block’s
        hash depends on the previous block’s hash, even a tiny change in an
        earlier block would ripple through the entire chain, invalidating every
        block that follows. To carry out such an attack, a malicious actor would
        not only have to redo the proof of work for the tampered block but also
        for all subsequent blocks, and they would need to do it faster than the
        honest miners who continue to add blocks to the chain.
        <br />
        This is why Bitcoin is often described as immutable. The longer a block
        has been buried under new blocks, the harder it is to reverse. It would
        take an astronomical amount of energy and computational resources to
        rewrite history, making Bitcoin’s ledger one of the most secure
        databases in the world.
        <br />
        <br />
        <b>The Evolution of Bitcoin Mining</b>
        <br />
        <br />
        In the earliest days of Bitcoin, anyone could mine using just their
        personal computer. The software for running a node, which keeps a copy
        of the entire blockchain and validates all transactions, was the same
        software used for mining. Bitcoin was a grassroots experiment, and
        enthusiasts would often run a full node and mine blocks using their
        CPUs, the basic processor in a computer.
        <br />
        As more people joined the network and competition increased, miners
        sought an edge. The first major leap was the move to GPUs, graphics
        cards, which could solve hashes far faster than CPUs. Then came FPGAs,
        field programmable gate arrays, followed by ASICs, application specific
        integrated circuits, machines designed for one purpose only: mining
        Bitcoin.
        <br />
        Today, mining is a highly specialized industry. Miners operate in large
        scale farms with racks of ASIC devices, often located in areas with
        cheap electricity and cold climates to keep machines cool. Mining pools
        have also emerged, where individuals combine their computational power
        and share rewards proportionally, allowing even small participants to
        earn consistent payouts.
        <br />
        Meanwhile, most users no longer mine and instead run full nodes to
        contribute to the decentralization and verification of the network
        without the massive electricity costs associated with mining.
        <br />
        <br />
        <b>The Role of the Mempool</b>
        <br />
        <br />
        Before transactions make it into a block, they sit in a sort of waiting
        room called the mempool, short for memory pool. Every full node
        maintains its own mempool, which contains valid transactions that have
        been broadcast to the network but not yet confirmed.
        <br />
        Miners select transactions from their mempool to include in the next
        block. But they do not do it randomly, they are financially incentivized
        to prioritize transactions offering higher fees. Users can attach a
        transaction fee to incentivize quicker processing. When blocks are full,
        miners typically sort transactions by fee rate, satoshis per byte,
        packing in the most lucrative transactions first.
        <br />
        This dynamic creates a market for block space. During times of high
        demand, users must compete with each other by increasing their fees to
        get prioritized. When demand is low, even transactions with minimal fees
        can get confirmed quickly.
        <br />
        <br />
        <b>Why Mining Matters</b>
        <br />
        <br />
        Bitcoin mining is a remarkable blend of economics, computer science, and
        game theory. It transforms electricity and hardware into digital trust,
        removing the need for intermediaries. It also aligns incentives in such
        a way that every participant, from the miner in Iceland to the node
        operator in Nigeria, contributes to the health and security of the
        system.
        <br />
        By understanding mining, one begins to appreciate the genius of
        Bitcoin’s design. It is more than just code. It is a living, breathing
        network that continuously adjusts, defends itself, and remains open to
        anyone in the world.
        <br />
        As we move forward into a future of digital money, the process of mining
        will remain a cornerstone of Bitcoin’s operation, an ever evolving dance
        between mathematics, machines, and human ingenuity.
        <br />
        <br />
        <b>Miners Serve the Network, They Do Not Rule It</b>
        <br />
        <br />
        While miners are essential to Bitcoin's operation and security, they do
        not govern its rules or direction. The real authority lies with full
        nodes. These are the parts of the network that independently verify
        every transaction and block according to Bitcoin's consensus rules.
        Miners are responsible for assembling and proposing blocks, but those
        blocks are only accepted if they meet the standards enforced by full
        nodes.
        <br />
        This creates a dynamic similar to a manager and a worker. Full nodes act
        as the managers, setting the criteria that miners must follow. Miners do
        the computational work of finding valid blocks, and full nodes validate
        that work before it can be added to the blockchain. Without this
        validation step, miners could make mistakes or act dishonestly by
        including invalid transactions or trying to manipulate block sizes for
        personal gain. Full nodes prevent such behavior by strictly enforcing
        the network's rules.
        <br />
        As discussed above, in the early days, Bitcoin software combined both
        mining and full node functionality in a single program. Over time, as
        mining became more specialized and resource intensive, the two functions
        split. Mining moved away from everyday users and toward industrial
        operations using powerful machines built only for mining. This change
        was not part of the original design but emerged naturally as competition
        and difficulty increased. Today, most users run full nodes, while
        professional miners focus on block production.
        <br />
        This separation has raised ongoing questions about who truly shapes
        Bitcoin's future. Is it the developers who write the software? The users
        who run full nodes? Or the miners who secure the network? The answer is
        that no single group has complete control. Bitcoin aims for
        decentralization, though it cannot be perfect. Instead, the system
        relies on incentives to balance power across participants.
        <br />
        Bitcoin's security and long term stability come from game theory. Miners
        are driven by self interest, and the network channels that drive into
        securing the system. They spend money and energy to find valid blocks,
        and they are rewarded in bitcoin. This reward structure keeps miners
        honest and productive. If any group, whether miners, developers, or
        users tries to change the rules for selfish gain, it can harm the entire
        network.
        <br />
        This is what makes Bitcoin resilient. It does not depend on trust in any
        single party. It depends on a system of incentives where everyone
        benefits by cooperating. Miners do not control Bitcoin. They provide a
        service, submitting their work to full nodes, which ultimately decide
        whether the work follows the rules.
      </p>
      <h2>Test your understanding</h2>

      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default BitcoinTechPage77;
